/**
 * Navigation Filters Utility Functions
 *
 * Single responsibility: Pure functions for filtering candidate frames
 * No side effects, no state - just filtering logic
 * Used by DirectHitStrategy and test utilities
 */

import { NavigationSettings, FrameFilterMetadata, FilterResult } from '../types/NavigationSettings'

/**
 * Check if frame passes 3D distance filter
 *
 * @param metadata Frame metadata with distance3d
 * @param maxDistance Max allowed 3D distance
 * @returns true if frame passes filter (distance <= maxDistance)
 */
export function passesThreeDistanceFilter(
  metadata: FrameFilterMetadata,
  maxDistance: number
): boolean {
  if (!metadata || metadata.distance3d === undefined) {
    // No 3D distance data available - can't filter
    return true
  }
  return metadata.distance3d <= maxDistance
}

/**
 * Check if frame passes delta angle filter
 *
 * @param metadata Frame metadata with angleDiff
 * @param maxAngle Max allowed angle difference (degrees)
 * @returns true if frame passes filter (angle <= maxAngle)
 */
export function passesDeltaAngleFilter(
  metadata: FrameFilterMetadata,
  maxAngle: number
): boolean {
  if (!metadata || metadata.angleDiff === undefined) {
    // No angle data available - can't filter
    return true
  }
  return metadata.angleDiff <= maxAngle
}

/**
 * Check if frame passes connectivity filter
 *
 * @param metadata Frame metadata with isConnected flag
 * @returns true if frame is connected (or if connection status unknown)
 */
export function passesConnectivityFilter(metadata: FrameFilterMetadata): boolean {
  // Defensive null check - isConnected should always be defined
  if (!metadata || metadata.isConnected === undefined) {
    // Unknown connectivity - assume connected (don't reject)
    return true
  }
  return metadata.isConnected
}

/**
 * Apply all enabled filters to candidate frames
 *
 * @param candidates Array of frame metadata to filter
 * @param settings Navigation settings with active filters
 * @returns FilterResult with valid frames and rejection reasons
 */
export function applyFilters(
  candidates: FrameFilterMetadata[],
  settings: NavigationSettings,
  currentFrameIndex?: number
): FilterResult {
  const validFrames: number[] = []
  const rejectionReasons = new Map<number, string[]>()

  const filtersApplied = {
    connectivity: settings.useConnectivityFiltering,
    threeDistance: settings.use3DDistanceFilter,
    deltaAngle: settings.useDeltaAngleFilter,
    maxCandidates: settings.maxCandidates !== null,
  }

  for (const metadata of candidates) {
    // Skip if metadata is undefined or null
    if (!metadata) {
      continue
    }

    const reasons: string[] = []

    // Check enforceMaxJumpDistance filter (NEW: added to prevent far jumps)
    if (settings.enforceMaxJumpDistance && currentFrameIndex !== undefined) {
      const distance = Math.abs(metadata.frameIndex - currentFrameIndex)
      const maxDistance = settings.nearWindow ?? 10
      if (distance > maxDistance) {
        reasons.push(`Distance ${distance} > max ${maxDistance}`)
        // console.log(`  🔍 Filtering out frame ${metadata.frameIndex}: distance ${distance} > nearWindow ${maxDistance}`)
      }
    }

    // Check connectivity filter
    if (settings.useConnectivityFiltering && !passesConnectivityFilter(metadata)) {
      reasons.push(`Not connected to current frame`)
    }

    // Check 3D distance filter
    if (settings.use3DDistanceFilter && !passesThreeDistanceFilter(metadata, settings.max3DDistance)) {
      reasons.push(`3D distance ${metadata.distance3d?.toFixed(2)} > ${settings.max3DDistance}`)
    }

    // Check delta angle filter
    if (settings.useDeltaAngleFilter && !passesDeltaAngleFilter(metadata, settings.maxDeltaAngle)) {
      reasons.push(`Angle difference ${metadata.angleDiff?.toFixed(1)}° > ${settings.maxDeltaAngle}°`)
    }

    if (reasons.length === 0) {
      validFrames.push(metadata.frameIndex)
    } else {
      rejectionReasons.set(metadata.frameIndex, reasons)
    }
  }

  // Apply max candidates limit if set
  let finalFrames = validFrames
  if (settings.maxCandidates !== null && validFrames.length > settings.maxCandidates) {
    finalFrames = validFrames.slice(0, settings.maxCandidates)
    // Note: We're just truncating, which is fine for limiting pool
    // In practice, caller should handle ranking to pick best maxCandidates
  }

  return {
    validFrames: finalFrames,
    rejectionReasons,
    filtersApplied,
  }
}

/**
 * Progressively relax filters until candidates found
 * Used for 'less-restrictive' fallback strategy
 *
 * @param candidates Array of frame metadata
 * @param originalSettings Original settings with all filters
 * @param currentFrameIndex Current frame index for distance checking
 * @returns First successful FilterResult with relaxed settings, plus reason for relaxation
 */
export function applyFallbackFilters(
  candidates: FrameFilterMetadata[],
  originalSettings: NavigationSettings,
  currentFrameIndex?: number
): { result: FilterResult; reason?: string } {
  // Try original filters first
  let result = applyFilters(candidates, originalSettings, currentFrameIndex)
  if (result.validFrames.length > 0) {
    return { result }
  }

  // Fallback 1: Disable delta angle filter
  if (originalSettings.useDeltaAngleFilter) {
    const relaxed = { ...originalSettings, useDeltaAngleFilter: false }
    result = applyFilters(candidates, relaxed, currentFrameIndex)
    if (result.validFrames.length > 0) {
      return { result, reason: 'Disabled delta angle filter' }
    }
  }

  // Fallback 2: Disable 3D distance filter
  if (originalSettings.use3DDistanceFilter) {
    const relaxed = { ...originalSettings, use3DDistanceFilter: false }
    result = applyFilters(candidates, relaxed, currentFrameIndex)
    if (result.validFrames.length > 0) {
      return { result, reason: 'Disabled 3D distance filter' }
    }
  }

  // Fallback 3: Disable connectivity filter
  if (originalSettings.useConnectivityFiltering) {
    const relaxed = { ...originalSettings, useConnectivityFiltering: false }
    result = applyFilters(candidates, relaxed, currentFrameIndex)
    if (result.validFrames.length > 0) {
      return { result, reason: 'Disabled connectivity filter' }
    }
  }

  // Fallback 4: Disable all filters except distance threshold
  const unrestricted = {
    ...originalSettings,
    useConnectivityFiltering: false,
    use3DDistanceFilter: false,
    useDeltaAngleFilter: false,
  }
  result = applyFilters(candidates, unrestricted, currentFrameIndex)
  if (result.validFrames.length > 0) {
    return { result, reason: 'Disabled all filters' }
  }

  // No candidates even after disabling all filters
  return { result }
}

/**
 * Convert distance threshold percent to pixels
 *
 * @param percentOfWidth Threshold as percentage of frame width (0-100)
 * @param frameWidth Frame width in pixels (default 450)
 * @returns Threshold in pixels
 */
export function convertThresholdPercentToPixels(
  percentOfWidth: number,
  frameWidth: number = 450
): number {
  return (percentOfWidth / 100) * frameWidth
}

/**
 * Convert distance threshold pixels to percent
 *
 * @param pixels Threshold in pixels
 * @param frameWidth Frame width in pixels (default 450)
 * @returns Threshold as percentage of frame width (0-100)
 */
export function convertThresholdPixelsToPercent(
  pixels: number,
  frameWidth: number = 450
): number {
  return (pixels / frameWidth) * 100
}

/**
 * Validate that frame metadata has required fields for given settings
 * Returns warning messages if critical data is missing
 *
 * @param metadata Frame metadata to validate
 * @param settings Navigation settings to check against
 * @returns Array of warning messages (empty if all valid)
 */
export function validateFrameMetadata(
  metadata: FrameFilterMetadata,
  settings: NavigationSettings
): string[] {
  const warnings: string[] = []

  if (settings.use3DDistanceFilter && metadata.distance3d === undefined) {
    warnings.push(`Frame ${metadata.frameIndex}: 3D distance filter enabled but distance3d missing`)
  }

  if (settings.useDeltaAngleFilter && metadata.angleDiff === undefined) {
    warnings.push(`Frame ${metadata.frameIndex}: Delta angle filter enabled but angleDiff missing`)
  }

  if (settings.useConnectivityFiltering && metadata.isConnected === undefined) {
    warnings.push(`Frame ${metadata.frameIndex}: Connectivity filter enabled but isConnected status missing`)
  }

  return warnings
}

/**
 * Get human-readable explanation for why a frame was rejected
 *
 * @param reasons Array of rejection reasons
 * @returns Single formatted string
 */
export function formatRejectionReasons(reasons: string[]): string {
  return reasons.join('; ')
}
